import axios from "axios";
import { baseURL } from "./APIConfig";
import { Message } from "../components/AlertMessage/Message";
import Swal from "sweetalert2";
import store from "@/redux/store";
import { attachAxiosErrorLogger, logApiError } from "@/services/apiErrorLogger";

let isAlertShown = false;

const axiosInstance = axios.create({
    baseURL: baseURL.live,
    timeout: 15000, // 15 seconds timeout
    timeoutErrorMessage: 'Request timeout - Server is taking too long to respond'
});

attachAxiosErrorLogger(axiosInstance, 'main-api', {
    skip: (error) => Boolean(error.config?._fromAPICall),
});

// axiosInstance.interceptors.request.use(
//     (config) => {
//         const token = localStorage.getItem('authToken');
//         if (token) {
//             config.headers['Authorization'] = `Bearer ${token}`;
//         }
//         return config;
//     },
//     (error) => {
//         Message('Try Again!', "Something went wrong - Data (Request) sending error", "warning");
//     }
// );


axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        const statusCode = error.response?.status;
        const isNetworkError = !error.response;
        const isTimeoutError = error.code === 'ECONNABORTED' || error.message.includes('timeout');

        // Log network/timeout errors for debugging
        if (isNetworkError || isTimeoutError) {
            console.error(`🔴 Network/Timeout Error:`, {
                code: error.code,
                message: error.message,
                url: error.config?.url,
                isTimeout: isTimeoutError,
                isNetwork: isNetworkError
            });
        }

        if (!isAlertShown && (statusCode === 401)) {
            localStorage.removeItem('authToken');
            window.location.href = "/";
        }
        console.log(error.response);

        // Only show blocking modal for 5xx server errors (not network errors, as retries will handle them)
        if (!isAlertShown && statusCode >= 500) {
            isAlertShown = true;
            Swal.fire({
                title: 'Server Error',
                text: (error.response?.message ?? error.response?.data?.message) || 'Something went wrong on the server.',
                icon: 'warning',
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'OK'
            }).then((result) => {
                isAlertShown = false;
            });
        }

        return Promise.reject(error);
    }
);

// const getFormData = (object) => {
//     const formData = new FormData();
//     Object.keys(object).forEach((key) => {
//         formData.append(key, object[key]);
//     });
//     return formData;
// };


/**
 * Retry logic with exponential backoff
 */
const retryRequest = async (config, maxRetries = 3, retryDelay = 1000) => {
    for (let i = 0; i < maxRetries; i++) {
        try {
            const response = await axiosInstance(config);
            if (i > 0) {
                console.log(`✅ Request succeeded on retry ${i}`);
            }
            return response;
        } catch (error) {
            const isLastAttempt = i === maxRetries - 1;
            const isTimeoutError = error.code === 'ECONNABORTED' || error.message.includes('timeout');
            const isNetworkError = error.code === 'ENOTFOUND' || error.code === 'ECONNREFUSED' || !error.response;

            // Only retry on timeout or network errors, not on 4xx/5xx responses
            if ((isTimeoutError || isNetworkError) && !isLastAttempt) {
                const waitTime = retryDelay * Math.pow(2, i); // exponential backoff
                console.warn(`⚠️ Request failed (attempt ${i + 1}/${maxRetries}), retrying in ${waitTime}ms...`, {
                    error: error.message,
                    code: error.code
                });
                await new Promise(resolve => setTimeout(resolve, waitTime));
                continue;
            }

            throw error;
        }
    }
};

export const APICall = async (
    method = 'get',
    data = null,
    endpoint = '',
    options = {}
) => {
    try {
          // SUPPORT OBJECT STYLE + OLD STYLE
    if (typeof method === "object") {
      options = method.options || {};
      endpoint = method.endpoint || "";
      data = method.data || null;
      method = method.method || "get";
    }
        const token = localStorage.getItem('authToken');

        // ✅ get user safely (Redux → fallback localStorage)
        let user = store.getState()?.auth?.userDetails;

        if (Array.isArray(user)) {
            user = user[0];
        }

        if (!user) {
            try {
                const stored = localStorage.getItem("authUser");
                const parsed = stored ? JSON.parse(stored) : null;
                user = Array.isArray(parsed) ? parsed[0] : parsed;
            } catch {
                user = null;
            }
        }

        const companyId = user?.companyId;

        let updatedEndpoint = endpoint;
        const methodType = method?.toLowerCase?.() || 'get';

        // ✅ ONLY for GET → append companyId (no duplicates)
        if (companyId && methodType === "get") {
            const hasCompanyId = /[?&]CompanyId=/i.test(updatedEndpoint);

            if (!hasCompanyId) {
                updatedEndpoint += updatedEndpoint.includes("?")
                    ? `&CompanyId=${companyId}`
                    : `?CompanyId=${companyId}`;
            }
        }

        const isFormData = data instanceof FormData;

        const config = {
            method: methodType,
            maxBodyLength: Infinity,
            url: updatedEndpoint,
            data: data, // ✅ untouched (FormData / JSON safe)
            timeout: 15000,
            _fromAPICall: true,
            ...options,
            headers: {
                ...(isFormData ? {} : { "Content-Type": "application/json" }),
                ...options.headers,
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
        };

        const response = await retryRequest(config);
        return response.data;

    } catch (error) {
        const statusCode = error.response?.status || 500;
        const serverMessage = error.response?.data || error.message;
        const isNetworkError = !error.response;
        const isTimeoutError =
            error.code === 'ECONNABORTED' ||
            error.message.includes('timeout');

        console.error("❌ API Error:", {
            status: statusCode,
            message: serverMessage,
            url: error.config?.url,
            method: error.config?.method,
            code: error.code,
            isTimeout: isTimeoutError,
            isNetworkError,
        });

        if (statusCode === 401) {
            console.error("🔐 Authentication error, please log in again");
        }

        if (isNetworkError) {
            console.error("🌐 Network error: Cannot reach the server.");
        }

        if (isTimeoutError) {
            console.error("⏱️ Request timeout (>15s)");
        }
        logApiError(error, { source: 'main-api', endpoint });
        throw error;
    }
};


export { axiosInstance };